import { memo } from "react";
import { FileText, FileSpreadsheet, Image as ImageIcon, File, Paperclip, Archive } from "lucide-react";

/**
 * Parses user message content to extract document attachments and clean user text.
 * Handles:
 * 1. Backend multi-document format: "📄 [N Dokumen: file1.docx, file2.pdf, ...]"
 * 2. Backend single-document format: "📄 file1.pdf"
 * 3. Frontend local multi-file format: "[Lampiran: N Dokumen]\n- file1.docx\n- file2.pdf"
 * 4. Frontend local single-file format: "[Lampiran Dokumen: file1.docx]"
 * 
 * @param {string|object} rawContent
 * @returns {{ attachments: string[], textOnly: string }}
 */
export function parseUserMessageAttachments(rawContent) {
  if (typeof rawContent !== "string") {
    rawContent = typeof rawContent === "object" ? JSON.stringify(rawContent) : String(rawContent || "");
  }

  let text = rawContent;
  const attachments = [];

  // Pattern 1: Backend format multi-document: 📄 [N Dokumen: file1, file2, ...] or [N Dokumen: ...] or [Dokumen: ...]
  const multiDocRegex = /(?:📄\s*)?\[\s*(?:\d+\s+)?Dokumen:\s*([\s\S]+?)\]/i;
  const matchMulti = text.match(multiDocRegex);
  if (matchMulti) {
    const listStr = matchMulti[1].trim();
    // Split by lookbehind on file extensions to preserve commas inside filenames
    const rawFiles = listStr.split(/(?<=\.[a-zA-Z0-9]{2,5}),\s*/).map((s) => s.trim()).filter(Boolean);
    if (rawFiles.length > 0) {
      rawFiles.forEach((f) => attachments.push(f));
    } else {
      listStr.split(",").forEach((f) => {
        const clean = f.trim();
        if (clean) attachments.push(clean);
      });
    }
    text = text.replace(multiDocRegex, "");
  }

  // Pattern 2: Frontend format multi-file: [Lampiran: N Dokumen]\n- file1\n- file2
  const lampiranMultiRegex = /\[Lampiran:\s*\d+\s+Dokumen\]/i;
  if (lampiranMultiRegex.test(text)) {
    text = text.replace(lampiranMultiRegex, "");
    const lines = text.split("\n");
    const remainingLines = [];
    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed.startsWith("- ")) {
        const cleanFile = trimmed.replace(/^-\s*/, "").trim();
        if (cleanFile) attachments.push(cleanFile);
      } else {
        remainingLines.push(line);
      }
    }
    text = remainingLines.join("\n");
  }

  // Pattern 3: Frontend format single file: [Lampiran Dokumen: filename.ext] or [Lampiran: filename.ext]
  const lampiranSingleRegex = /\[Lampiran(?:\s+Dokumen)?:\s*(.+?)\]/gi;
  let matchSingle;
  while ((matchSingle = lampiranSingleRegex.exec(text)) !== null) {
    const val = matchSingle[1].trim();
    if (val && !/^\d+\s+Dokumen$/i.test(val)) {
      attachments.push(val);
    }
  }
  text = text.replace(lampiranSingleRegex, "");

  // Pattern 4: Backend format single document: 📄 filename.ext
  const singleDocRegex = /(?:^|\n)\s*📄\s*([^\n\r]+?\.[a-zA-Z0-9]{2,5})\s*(?=\n|$)/g;
  let matchSingleDoc;
  while ((matchSingleDoc = singleDocRegex.exec(text)) !== null) {
    const val = matchSingleDoc[1].trim();
    if (val) attachments.push(val);
  }
  text = text.replace(singleDocRegex, "\n");

  // Clean up any remaining leading/trailing whitespace & newlines
  const textOnly = text.trim();

  // Deduplicate attachments while preserving order
  const uniqueAttachments = Array.from(new Set(attachments));

  return { attachments: uniqueAttachments, textOnly };
}

/**
 * Returns icon, color styling, and category for a given filename.
 */
function getFileMetadata(name) {
  const ext = (name.split(".").pop() || "").toLowerCase();

  if (["docx", "doc", "rtf", "odt"].includes(ext)) {
    return {
      icon: FileText,
      label: ext.toUpperCase(),
      category: "Dokumen Word",
      iconBox: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/25",
      badge: "bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/20",
    };
  }

  if (["pdf"].includes(ext)) {
    return {
      icon: FileText,
      label: "PDF",
      category: "Dokumen PDF",
      iconBox: "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/25",
      badge: "bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/20",
    };
  }

  if (["xlsx", "xls", "csv", "ods"].includes(ext)) {
    return {
      icon: FileSpreadsheet,
      label: ext.toUpperCase(),
      category: "Lembar Kerja Excel",
      iconBox: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/25",
      badge: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20",
    };
  }

  if (["png", "jpg", "jpeg", "webp", "gif", "svg"].includes(ext)) {
    return {
      icon: ImageIcon,
      label: ext.toUpperCase(),
      category: "Berkas Gambar",
      iconBox: "bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/25",
      badge: "bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/20",
    };
  }

  if (["zip", "rar", "7z", "tar", "gz"].includes(ext)) {
    return {
      icon: Archive,
      label: ext.toUpperCase(),
      category: "Arsip Berkas",
      iconBox: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/25",
      badge: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20",
    };
  }

  return {
    icon: File,
    label: ext.length <= 4 && ext.length > 0 ? ext.toUpperCase() : "FILE",
    category: "Berkas Dokumen",
    iconBox: "bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/25",
    badge: "bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-500/20",
  };
}

/**
 * Single document card component styled in modern ChatGPT / Claude attachment format.
 */
export const DocumentCard = memo(function DocumentCard({ filename }) {
  const meta = getFileMetadata(filename);
  const IconComponent = meta.icon;

  return (
    <div
      className="flex items-center gap-3 p-3 rounded-2xl bg-card-bg border border-ledger-border hover:border-brand/40 shadow-2xs hover:shadow-xs transition-all group/doc"
      title={filename}
    >
      {/* Large Document Icon */}
      <div className={`w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shrink-0 border ${meta.iconBox}`}>
        <IconComponent className="w-5 h-5 sm:w-6 sm:h-6" />
      </div>

      {/* Document Information */}
      <div className="min-w-0 flex-1 flex flex-col justify-center">
        <span className="text-[13px] sm:text-[13.5px] font-semibold text-text-pri truncate leading-tight group-hover/doc:text-brand transition-colors">
          {filename}
        </span>
        <div className="flex items-center gap-1.5 mt-1">
          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9.5px] font-mono font-bold tracking-wider uppercase border ${meta.badge}`}>
            {meta.label}
          </span>
          <span className="text-[11px] text-text-sec/80 truncate">
            {meta.category}
          </span>
        </div>
      </div>
    </div>
  );
});

/**
 * Renders a list/grid of document attachments with optional section header.
 */
export const DocumentAttachmentList = memo(function DocumentAttachmentList({ attachments }) {
  if (!attachments || attachments.length === 0) return null;

  const isMulti = attachments.length > 1;

  return (
    <div className="w-full mb-3">
      {isMulti && (
        <div className="flex items-center justify-between mb-2 px-0.5">
          <span className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-brand tracking-wide">
            <Paperclip className="w-3.5 h-3.5" />
            Lampiran Dokumen ({attachments.length})
          </span>
          <span className="text-[10.5px] text-text-sec/70 font-mono">
            Terverifikasi
          </span>
        </div>
      )}

      <div className={isMulti ? "grid grid-cols-1 sm:grid-cols-2 gap-2.5 w-full" : "w-full max-w-sm"}>
        {attachments.map((fname, i) => (
          <DocumentCard key={i} filename={fname} />
        ))}
      </div>
    </div>
  );
});

export default DocumentAttachmentList;
